import { createSelector } from '@reduxjs/toolkit';
import { DataStatus, isError, isLoading, isFinished } from '@/shared/lib';
import { createAppSlice } from '@/shared/model';
import * as SavedPresetsAPI from '../api/savedPresetsAPI';
import type { PresetViewType } from '../api/types';
import type { SavedPresetRecord } from './types';

interface State {
  presets: SavedPresetRecord[];
  status: DataStatus;
  requestedOrganizationId?: string;
}

const initialState: State = {
  presets: [],
  status: DataStatus.idle,
  requestedOrganizationId: undefined,
};

export const savedPresetsSlice = createAppSlice({
  name: 'savedPresets',
  initialState,
  reducers: (create) => ({
    setupPresets: create.asyncThunk<string, SavedPresetRecord[]>(
      () => {
        return SavedPresetsAPI.getSavedPresets();
      },
      {
        pending: (state, action) => {
          state.status = DataStatus.loading;
          state.requestedOrganizationId = action.meta.arg;
        },
        fulfilled: (state, action) => {
          state.presets = action.payload;
          state.status = DataStatus.finished;
        },
        rejected: (state) => {
          state.status = DataStatus.error;
          state.presets = [];
          state.requestedOrganizationId = undefined;
        },
        options: {
          condition: (requestedOrganizationId, { getState }) => {
            const state = getState() as RootState;

            if (
              state.savedPresets.requestedOrganizationId ===
                requestedOrganizationId &&
              (isLoading(state.savedPresets.status) ||
                isFinished(state.savedPresets.status))
            ) {
              return false;
            }
          },
        },
      },
    ),
    createPreset: create.asyncThunk<
      {
        viewType: PresetViewType;
        name: string;
        preset: unknown;
        isDefault: boolean;
        isFavorite: boolean;
      },
      SavedPresetRecord
    >(
      ({ viewType, name, preset, isDefault, isFavorite }) => {
        return SavedPresetsAPI.createSavedPreset({
          viewType,
          name,
          preset,
          isDefault,
          isFavorite,
        });
      },
      {
        fulfilled: (state, action) => {
          if (action.payload.isDefault) {
            state.presets = state.presets.map((p) =>
              p.isDefault && p.viewType === action.payload.viewType
                ? { ...p, isDefault: false }
                : p,
            );
          }
          state.presets.unshift(action.payload);
        },
      },
    ),
    updatePreset: create.asyncThunk<
      {
        id: string;
        update: {
          name?: string;
          viewType?: PresetViewType;
          preset?: unknown;
          isDefault?: boolean;
          isFavorite?: boolean;
        };
      },
      SavedPresetRecord
    >(
      ({ id, update }, { getState }) => {
        const state = getState() as RootState;
        const presets: SavedPresetRecord[] = selectPresets(state);
        const presetToUpdate = presets.find((preset) => {
          return preset.id === id;
        });

        if (!presetToUpdate) {
          throw new Error(`Update of unknown preset: ${id}`);
        }

        return SavedPresetsAPI.updateSavedPreset(id, update);
      },
      {
        pending: (state, action) => {
          state.presets = state.presets.map((preset) => {
            if (preset.id === action.meta.arg.id) {
              return {
                ...preset,
                updating: true,
              };
            }

            return preset;
          });
        },
        fulfilled: (state, action) => {
          if (action.payload.isDefault) {
            state.presets = state.presets.map((p) =>
              p.isDefault &&
              p.viewType === action.payload.viewType &&
              p.id !== action.payload.id
                ? { ...p, isDefault: false }
                : p,
            );
          }
          state.presets = state.presets.map((preset) => {
            if (preset.id === action.payload.id) {
              return { ...action.payload, isOwner: preset.isOwner };
            }

            return preset;
          });
        },
        rejected: (state, action) => {
          state.presets = state.presets.map((preset) => {
            if (preset.id === action.meta.arg.id) {
              return { ...preset, updating: false };
            }

            return preset;
          });
        },
      },
    ),
    deletePreset: create.asyncThunk(
      (id: string) => {
        return SavedPresetsAPI.deleteSavedPreset(id);
      },
      {
        pending: (state, action) => {
          state.presets = state.presets.map((preset) => {
            if (preset.id === action.meta.arg) {
              return {
                ...preset,
                updating: true,
              };
            }

            return preset;
          });
        },
        fulfilled: (state, action) => {
          state.presets = state.presets.filter((preset) => {
            return preset.id !== action.meta.arg;
          });
        },
        rejected: (state, action) => {
          state.presets = state.presets.map((preset) => {
            if (preset.id === action.meta.arg) {
              return { ...preset, updating: false };
            }

            return preset;
          });
        },
      },
    ),
    sharePreset: create.asyncThunk(
      (id: string) => {
        return SavedPresetsAPI.shareSavedPreset(id);
      },
      {
        pending: (state, action) => {
          state.presets = state.presets.map((preset) => {
            if (preset.id === action.meta.arg) {
              return {
                ...preset,
                updating: true,
              };
            }

            return preset;
          });
        },
        fulfilled: (state, action) => {
          state.presets = state.presets.map((preset) => {
            if (preset.id === action.meta.arg) {
              return { ...preset, share: action.payload, updating: false };
            }

            return preset;
          });
        },
        rejected: (state, action) => {
          state.presets = state.presets.map((preset) => {
            if (preset.id === action.meta.arg) {
              return { ...preset, updating: false };
            }

            return preset;
          });
        },
      },
    ),
    unsharePreset: create.asyncThunk(
      (id: string) => {
        return SavedPresetsAPI.unshareSavedPreset(id);
      },
      {
        pending: (state, action) => {
          state.presets = state.presets.map((preset) => {
            if (preset.id === action.meta.arg) {
              return {
                ...preset,
                updating: true,
              };
            }

            return preset;
          });
        },
        fulfilled: (state, action) => {
          state.presets = state.presets.map((preset) => {
            if (preset.id === action.meta.arg) {
              return { ...preset, share: null, updating: false };
            }

            return preset;
          });
        },
        rejected: (state, action) => {
          state.presets = state.presets.map((preset) => {
            if (preset.id === action.meta.arg) {
              return { ...preset, updating: false };
            }

            return preset;
          });
        },
      },
    ),
  }),
  selectors: {
    selectPresetById: createSelector(
      (state: State) => state.presets,
      (_, presetId: string) => {
        return presetId;
      },
      (presets: SavedPresetRecord[], presetId: string) => {
        return presets.find((preset) => {
          return preset.id === presetId;
        });
      },
    ),
    selectPresets: (state) => {
      return state.presets;
    },
    selectIsLoading: (state) => {
      return isLoading(state.status);
    },
    selectIsFailed: (state) => {
      return isError(state.status);
    },
    selectIsFinished: (state) => {
      return isFinished(state.status);
    },
    selectStatus: (state) => state.status,
  },
});

export const {
  setupPresets,
  createPreset,
  deletePreset,
  updatePreset,
  sharePreset,
  unsharePreset,
} = savedPresetsSlice.actions;
export const {
  selectIsFailed,
  selectIsLoading,
  selectPresets,
  selectIsFinished,
  selectPresetById,
  selectStatus,
} = savedPresetsSlice.selectors;

